/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CluUtteranceEntitySwagger, CluUtteranceSwagger } from 'src/clu/core/services/cluUtteranceSwagger';
import { CluProjectType } from 'src/clu/core/services/projects/cluProjectSwaggers';
import { CluIntentStore } from 'src/clu/core/stores/CluIntentStore';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { EntityStore } from 'src/core/types/EntityStore';
import { DistributionBar, DistributionTypes } from 'src/modules/dataDistribution/DistributionBar';
import styled from 'styled-components';

export type CluDataDistributionProps = {
    intents: CluIntentStore[];
    entities: EntityStore[];
    projectType: CluProjectType;
    utterances: CluUtteranceSwagger[];
};

const messages = defineMessages({
    distributionTitle: { id: 'clu.cluDataDistribution.distributionTitle', defaultMessage: 'Activity pane' },
    intentsOption: { id: 'clu.cluDataDistribution.intentsOption', defaultMessage: 'Utterances per intent' },
    linkedIntentsOption: { id: 'clu.cluDataDistribution.linkedIntentsOption', defaultMessage: 'Connected intents' },
    testingDatasetLabel: { id: 'clu.cluDataDistribution.testingDatasetLabel', defaultMessage: 'Testing dataset ({count})' },
    nonLinkedIntentsOption: { id: 'clu.cluDataDistribution.nonLinkedIntentsOption', defaultMessage: 'Not connected intents' },
    trainingDatasetLabel: { id: 'clu.cluDataDistribution.trainingDatasetLabel', defaultMessage: 'Training dataset  ({count})' },
    tagDistributionLabel: { id: 'clu.cluDataDistribution.tagDistributionLabel', defaultMessage: 'View label distribution across:' },
    entitiesInTagsOption: { id: 'clu.cluDataDistribution.entitiesInTagsOption', defaultMessage: 'Total instances per labeled entity' },
    entitiesInUtterancesOption: {
        id: 'clu.cluDataDistribution.entitiesInUtterancesOption',
        defaultMessage: 'Unique utterances per labeled entity'
    },
    emptyMessage: {
        id: 'clu.cluDataDistribution.emptyMessage',
        defaultMessage: 'No labels to show yet. Begin labeling to see how your labels are distributed.'
    },
    intentsDescription: {
        id: 'clu.cluDataDistribution.intentsDescription',
        defaultMessage: 'You are now viewing the data distribution across your intents for training and testing datasets'
    },
    entitiesInUtterancesDescription: {
        id: 'clu.cluDataDistribution.entitiesInUtterancesDescription',
        defaultMessage: 'You are now viewing the data distribution across your entities for training and testing datasets'
    }
});

const theme = getScopedTheme('CluDataDistribution');

const textStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({ root: { display: 'flex', textAlign: 'center' } });
const legendColors: { [key in DistributionTypes]: { training: string; testing: string } } = {
    intents: { training: theme.trainingIntentsColor, testing: theme.testingIntentColor },
    linkedIntents: { training: theme.trainingEntitiesInTagsColor, testing: theme.testingEntitiesInTagsColor },
    entitiesInTags: { training: theme.trainingEntitiesInTagsColor, testing: theme.testingEntitiesInTagsColor },
    nonLinkedIntents: { training: theme.trainingEntitiesInTagsColor, testing: theme.testingEntitiesInTagsColor },
    entitiesInUtterances: { training: theme.trainingEntitiesInUtterancesColor, testing: theme.testingEntitiesInUtterancesColor }
};

const Title = styled(EditableTitle)({
    padding: '5px 0 10px 16px',
    marginBottom: '0px',
    borderBottom: theme.borderSpecs
});
const getLegendIcon = (color: string) => <div style={{ background: color, width: '10px', height: '10px' }} />;

export const CluDataDistribution = (props: CluDataDistributionProps) => {
    const { utterances, intents, entities, projectType } = props;
    const { localize } = useLocalization();

    const [selectedDistribution, setSelectedDistribution] = React.useState<DistributionTypes>(
        projectType === 'Conversation' ? 'intents' : 'nonLinkedIntents'
    );

    const tagDistributionOptions: IDropdownOption[] = [
        ...(projectType === 'Conversation'
            ? [
                  { key: 'intents', text: localize(messages.intentsOption) },
                  { key: 'entitiesInUtterances', text: localize(messages.entitiesInUtterancesOption) },
                  { key: 'entitiesInTags', text: localize(messages.entitiesInTagsOption) }
              ]
            : [
                  { key: 'linkedIntents', text: localize(messages.linkedIntentsOption) },
                  { key: 'nonLinkedIntents', text: localize(messages.nonLinkedIntentsOption) }
              ])
    ];
    const linkedIntents = intents.filter(intent => intent.orchestrationType);
    const testingUtterances = utterances.filter(u => (u.dataset || 'Train') === 'Test');
    const trainingUtterances = utterances.filter(u => (u.dataset || 'Train') === 'Train');
    const testingTags: CluUtteranceEntitySwagger[] = testingUtterances.reduce((acc, utterance) => [...acc, ...utterance.entities], []);
    const trainingTags: CluUtteranceEntitySwagger[] = trainingUtterances.reduce((acc, utterance) => [...acc, ...utterance.entities], []);
    const distributedIntents = (filterPredicate: (intent: CluIntentStore) => boolean = () => true) =>
        intents.filter(filterPredicate).reduce((acc, intent, intentIndex) => {
            const testingCount = testingUtterances.filter(u => u.intent === intent.name).length;
            const trainingCount = trainingUtterances.filter(u => u.intent === intent.name).length;

            return [
                ...acc,
                <DistributionBar
                    text={intent.name}
                    testingCount={testingCount}
                    trainingCount={trainingCount}
                    displaySeparator={intentIndex !== intents.length - 1}
                    testingColor={legendColors[selectedDistribution].testing}
                    trainingColor={legendColors[selectedDistribution].training}
                    testingPercent={(testingCount / testingUtterances.length) * 100 || 0}
                    trainingPercent={(trainingCount / trainingUtterances.length) * 100 || 0}
                />
            ];
        }, []);
    const distributedEntitiesInTags = entities.reduce((acc, entity, entityIndex) => {
        const testingCount = testingTags.filter(u => u.category === entity.name).length;
        const trainingCount = trainingTags.filter(u => u.category === entity.name).length;

        return [
            ...acc,
            <DistributionBar
                text={entity.name}
                testingCount={testingCount}
                trainingCount={trainingCount}
                displaySeparator={entityIndex !== entities.length - 1}
                testingColor={legendColors[selectedDistribution].testing}
                trainingColor={legendColors[selectedDistribution].training}
                testingPercent={(testingCount / testingTags.length) * 100 || 0}
                trainingPercent={(trainingCount / trainingTags.length) * 100 || 0}
            />
        ];
    }, []);
    const distributedEntitiesInUtterances = entities.reduce((acc, entity, entityIndex) => {
        const testingCount = testingUtterances.filter(u => u.entities.some(e => e.category === entity.name)).length;
        const trainingCount = trainingUtterances.filter(u => u.entities.some(e => e.category === entity.name)).length;

        return [
            ...acc,
            <DistributionBar
                text={entity.name}
                testingCount={testingCount}
                trainingCount={trainingCount}
                displaySeparator={entityIndex !== entities.length - 1}
                testingColor={legendColors[selectedDistribution].testing}
                trainingColor={legendColors[selectedDistribution].training}
                testingPercent={(testingCount / testingUtterances.length) * 100 || 0}
                trainingPercent={(trainingCount / trainingUtterances.length) * 100 || 0}
            />
        ];
    }, []);

    let testingCountToDisplay = 0;
    let trainingCountToDisplay = 0;

    switch (selectedDistribution) {
        case 'entitiesInTags':
            testingCountToDisplay = testingTags.length;
            trainingCountToDisplay = trainingTags.length;
            break;
        case 'nonLinkedIntents':
            testingCountToDisplay = testingUtterances.filter(u => linkedIntents.every(i => i.name !== u.intent)).length;
            trainingCountToDisplay = trainingUtterances.filter(u => linkedIntents.every(i => i.name !== u.intent)).length;
            break;
        case 'linkedIntents':
            testingCountToDisplay = testingUtterances.filter(u => linkedIntents.some(i => i.name === u.intent)).length;
            trainingCountToDisplay = trainingUtterances.filter(u => linkedIntents.some(i => i.name === u.intent)).length;
            break;
        case 'entitiesInUtterances':
        case 'intents':
        default:
            testingCountToDisplay = testingUtterances.length;
            trainingCountToDisplay = trainingUtterances.length;
    }

    const description = () => {
        switch (selectedDistribution) {
            case 'entitiesInTags':
            case 'entitiesInUtterances':
                return localize(messages.entitiesInUtterancesDescription);
            case 'intents':
                return localize(messages.intentsDescription);
            default:
        }
    };

    const listToRender = () => {
        switch (selectedDistribution) {
            case 'intents':
                return distributedIntents();
            case 'entitiesInTags':
                return distributedEntitiesInTags;
            case 'nonLinkedIntents':
                return distributedIntents(intent => linkedIntents.every(i => i.name !== intent.name));
            case 'linkedIntents':
                return distributedIntents(intent => linkedIntents.some(i => i.name === intent.name));
            case 'entitiesInUtterances':
            default:
                return distributedEntitiesInUtterances;
        }
    };

    return (
        <Stack verticalFill>
            {projectType === 'Orchestration' && <Title title={localize(messages.distributionTitle)} />}
            {utterances.length === 0 ? (
                <EmptyStateView
                    imageStyles={{ image: { width: '150px' } }}
                    message={<LuisText styles={textStyles}>{localize(messages.emptyMessage)}</LuisText>}
                />
            ) : (
                <Stack verticalFill tokens={{ childrenGap: 10, padding: '0 0 16px 16px' }}>
                    <Stack tokens={{ childrenGap: 10, padding: '0 16px 0 0px' }}>
                        <LuisDropdown
                            options={tagDistributionOptions}
                            selectedKey={selectedDistribution}
                            label={localize(messages.tagDistributionLabel)}
                            onChange={(_, option) => setSelectedDistribution(option.key as DistributionTypes)}
                        />

                        <LuisText>{description()}</LuisText>

                        <Stack tokens={{ childrenGap: 5 }}>
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                                {getLegendIcon(legendColors[selectedDistribution].training)}
                                <LuisText>{localize(messages.trainingDatasetLabel, { count: trainingCountToDisplay })}</LuisText>
                            </Stack>
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                                {getLegendIcon(legendColors[selectedDistribution].testing)}
                                <LuisText>{localize(messages.testingDatasetLabel, { count: testingCountToDisplay })}</LuisText>
                            </Stack>
                        </Stack>
                    </Stack>

                    <Stack verticalFill styles={{ root: { position: 'relative' } }} tokens={{ childrenGap: 5 }}>
                        <ScrollablePane styles={{ contentContainer: { paddingRight: '16px' } }} scrollContainerFocus scrollContainerAriaLabel={description()}>{listToRender()}</ScrollablePane>
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
};
